/**
 * One remote device session's life, for the dialog (design §7):
 *
 *   start (POST) → "starting" while admin and the deployer bring up the
 *   desktop (a cold node, the ~1 GB image and up to 90 s waiting for the
 *   device can add up to admin's 180 s start timeout) → poll GET until
 *   `ready` → a single-use token → the noVNC view connects → "connected".
 *
 * If admin says the device already has a session and it is ours
 * (409 `session_active` with `session_id`), we reattach to it.
 *
 * A dropped WebSocket (network blip, laptop sleep) asks for a fresh token
 * and reconnects, unless admin says the session is over. Closing the dialog
 * ends the session so the node is released.
 */
import React from "react";
import {
  endDeviceSession,
  endReasonText,
  fetchDeviceSession,
  fetchDeviceSessionToken,
  refusalText,
  startDeviceSession,
  startedSessionId,
} from "./deviceSessionApi";
import {
  DeviceSession,
  DeviceSessionBrowserIdentity,
  DeviceSessionCompatHint,
  DeviceSessionOutcome,
  DeviceSessionPolicy,
  DeviceSessionPortSource,
  DeviceSessionTarget,
} from "./deviceSessionTypes";

/** How often to ask whether the desktop is up. */
export const START_POLL_MS = 2000;
/**
 * Give up waiting for a cold start after this long: just past admin's own
 * 180 s start timeout, so admin's verdict normally arrives first.
 */
export const START_TIMEOUT_MS = 200 * 1000;
/** While connected, re-read the session for its deadline and end. */
export const LIVE_POLL_MS = 15 * 1000;
/** Reconnect attempts after an unexpected disconnect. */
export const MAX_RECONNECTS = 3;

export interface DeviceSessionCredentials {
  wsUrl: string;
  token: string;
}

export type DeviceSessionPhase =
  | { kind: "idle" }
  | { kind: "starting"; startedAt: number }
  | { kind: "connecting"; reconnect: boolean }
  | { kind: "connected" }
  | { kind: "refused"; message: string }
  | { kind: "ended"; message: string }
  | { kind: "failed"; message: string };

export interface DeviceSessionState {
  phase: DeviceSessionPhase;
  sessionId: string | null;
  credentials: DeviceSessionCredentials | null;
  expiresAt: string | null;
  policy: DeviceSessionPolicy;
  /** How the remote browser presents itself (ENG-2507). */
  browserIdentity: DeviceSessionBrowserIdentity;
  compatHint: DeviceSessionCompatHint | null;
  /** The device address and its allowed ports, from admin (may be absent). */
  targetIp: string | null;
  webPorts: DeviceSessionPortSource[];
  rtspPorts: DeviceSessionPortSource[];
  startPort: number | null;
  /** The effective start scheme from admin. */
  scheme: "http" | "https" | null;
}

const portList = (value: unknown): DeviceSessionPortSource[] =>
  Array.isArray(value)
    ? value.filter(
        (row): row is DeviceSessionPortSource =>
          !!row && typeof row === "object" && Number.isInteger(row.port)
      )
    : [];

const INITIAL: DeviceSessionState = {
  phase: { kind: "idle" },
  sessionId: null,
  credentials: null,
  expiresAt: null,
  policy: "customer",
  browserIdentity: "chromium",
  compatHint: null,
  targetIp: null,
  webPorts: [],
  rtspPorts: [],
  startPort: null,
  scheme: null,
};

/** Any non-ok outcome, in words. */
export const outcomeMessage = (
  outcome: Exclude<DeviceSessionOutcome<unknown>, { kind: "ok" }>
): string => {
  switch (outcome.kind) {
    case "refused":
      return refusalText(outcome);
    case "forbidden":
      return outcome.message;
    case "unavailable":
      return "Remote sessions aren't available yet.";
    case "not_found":
      return "This remote session no longer exists. Open a new one.";
    case "error":
      return outcome.message;
  }
};

const isOver = (session: DeviceSession) =>
  session.status === "ended" || session.status === "failed";

export const useDeviceSession = (
  target: DeviceSessionTarget | null,
  active: boolean
) => {
  const [state, setState] = React.useState<DeviceSessionState>(INITIAL);
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = React.useRef(true);
  const mounted = React.useRef(true);
  const generation = React.useRef(0);
  const reconnects = React.useRef(0);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The ref moves with every patch so async steps read the latest state.
  const update = React.useCallback((patch: Partial<DeviceSessionState>) => {
    if (!alive.current) return;
    stateRef.current = { ...stateRef.current, ...patch };
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const finish = React.useCallback(
    (session: DeviceSession) => {
      clearTimer();
      const kind = session.status === "failed" ? "failed" : "ended";
      update({
        phase: {
          kind,
          message: endReasonText(kind, session.reason, session.detail),
        },
        credentials: null,
      });
    },
    [update]
  );

  /** A fresh token (reconnects, or a GET that didn't carry one). */
  const connectWithToken = React.useCallback(
    async (id: string, reconnect: boolean) => {
      const outcome = await fetchDeviceSessionToken(id);
      if (!alive.current || stateRef.current.sessionId !== id) return;
      if (outcome.kind !== "ok") {
        clearTimer();
        update({
          phase: { kind: "failed", message: outcomeMessage(outcome) },
          credentials: null,
        });
        return;
      }
      update({
        phase: { kind: "connecting", reconnect },
        credentials: { wsUrl: outcome.data.ws_url, token: outcome.data.token },
        ...(outcome.data.expires_at
          ? { expiresAt: outcome.data.expires_at }
          : {}),
        ...(outcome.data.policy ? { policy: outcome.data.policy } : {}),
      });
    },
    [update]
  );

  const poll = React.useCallback(
    async (id: string, startedAt: number) => {
      const outcome = await fetchDeviceSession(id);
      if (!alive.current || stateRef.current.sessionId !== id) return;
      const phase = stateRef.current.phase.kind;
      // Ended or failed meanwhile (End clicked): stop polling.
      if (
        phase !== "starting" &&
        phase !== "connecting" &&
        phase !== "connected"
      ) {
        return;
      }
      if (outcome.kind === "ok") {
        const session = outcome.data;
        if (isOver(session)) {
          finish(session);
          return;
        }
        const patch: Partial<DeviceSessionState> = {};
        if (session.expires_at) patch.expiresAt = session.expires_at;
        if (session.policy) patch.policy = session.policy;
        patch.browserIdentity =
          session.browser_identity === "edge" ? "edge" : "chromium";
        patch.compatHint = session.compat_hint ?? null;
        if (session.target_ip) patch.targetIp = session.target_ip;
        if (session.web_port_sources)
          patch.webPorts = portList(session.web_port_sources);
        if (session.rtsp_port_sources)
          patch.rtspPorts = portList(session.rtsp_port_sources);
        if (typeof session.start_port === "number")
          patch.startPort = session.start_port;
        if (session.scheme === "http" || session.scheme === "https")
          patch.scheme = session.scheme;
        if (phase === "starting" && session.status !== "starting") {
          if (session.ws_url && session.token) {
            update({
              ...patch,
              phase: { kind: "connecting", reconnect: false },
              credentials: { wsUrl: session.ws_url, token: session.token },
            });
          } else {
            update(patch);
            await connectWithToken(id, false);
          }
        } else {
          update(patch);
        }
      } else if (outcome.kind === "not_found" || outcome.kind === "forbidden") {
        finish({ status: "ended", reason: null });
        return;
      }
      // A transient error while polling: keep going.
      if (!alive.current || stateRef.current.sessionId !== id) return;
      const now = Date.now();
      if (
        stateRef.current.phase.kind === "starting" &&
        now - startedAt > START_TIMEOUT_MS
      ) {
        finish({ status: "failed", reason: "start_timeout" });
        endDeviceSession(id);
        return;
      }
      const next =
        stateRef.current.phase.kind === "starting"
          ? START_POLL_MS
          : LIVE_POLL_MS;
      clearTimer();
      timer.current = setTimeout(() => poll(id, startedAt), next);
    },
    [connectWithToken, finish, update]
  );

  const start = React.useCallback(async () => {
    if (!target) return;
    clearTimer();
    reconnects.current = 0;
    const gen = ++generation.current;
    const startedAt = Date.now();
    stateRef.current = { ...INITIAL, phase: { kind: "starting", startedAt } };
    setState(stateRef.current);
    const outcome = await startDeviceSession(target);
    // Our own session already open on this device (another tab, a reload):
    // reattach rather than refuse.
    const id =
      outcome.kind === "ok"
        ? startedSessionId(outcome.data)
        : outcome.kind === "refused" && outcome.code === "session_active"
        ? outcome.sessionId ?? null
        : null;
    if (!alive.current || gen !== generation.current) {
      // Closed (or restarted) while starting: release what we started.
      if (outcome.kind === "ok" && id) endDeviceSession(id);
      return;
    }
    if (!id) {
      update({
        phase: {
          kind: "refused",
          message:
            outcome.kind === "ok"
              ? "We couldn't open the device. Try again later."
              : outcomeMessage(outcome),
        },
      });
      return;
    }
    update({ sessionId: id });
    poll(id, startedAt);
  }, [poll, target, update]);

  /** The noVNC view reports in. */
  const onConnected = React.useCallback(() => {
    reconnects.current = 0;
    update({ phase: { kind: "connected" } });
  }, [update]);

  const onDisconnected = React.useCallback(async () => {
    const id = stateRef.current.sessionId;
    const kind = stateRef.current.phase.kind;
    if (!id || (kind !== "connected" && kind !== "connecting")) return;
    update({ credentials: null });
    // Over (idle, deadline, ended elsewhere)? Then say so, don't reconnect.
    const outcome = await fetchDeviceSession(id);
    if (!alive.current || stateRef.current.sessionId !== id) return;
    if (outcome.kind === "ok" && isOver(outcome.data)) {
      finish(outcome.data);
      return;
    }
    if (outcome.kind === "not_found") {
      finish({ status: "ended", reason: null });
      return;
    }
    if (reconnects.current >= MAX_RECONNECTS) {
      clearTimer();
      update({
        phase: {
          kind: "failed",
          message:
            "We lost the connection to the remote session and couldn't reconnect. Check your internet connection, then open the device again.",
        },
      });
      endDeviceSession(id);
      return;
    }
    reconnects.current += 1;
    await connectWithToken(id, true);
  }, [connectWithToken, finish, update]);

  const end = React.useCallback(async () => {
    const id = stateRef.current.sessionId;
    clearTimer();
    update({
      phase: { kind: "ended", message: endReasonText("ended", "user") },
      credentials: null,
    });
    if (id) await endDeviceSession(id);
  }, [update]);

  // Start when opened; end (best effort) when closed or unmounted.
  React.useEffect(() => {
    alive.current = true;
    if (active && target) start();
    return () => {
      const id = stateRef.current.sessionId;
      const kind = stateRef.current.phase.kind;
      clearTimer();
      generation.current += 1;
      if (id && kind !== "ended" && kind !== "failed") endDeviceSession(id);
      alive.current = false;
      stateRef.current = INITIAL;
      if (mounted.current) setState(INITIAL);
    };
    // A new target means a new session; `start` changes with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, target?.siteId, target?.source, target?.ref]);

  /** After a browser switch admin accepted, before the next poll says so. */
  const setBrowserIdentity = React.useCallback(
    (browserIdentity: DeviceSessionBrowserIdentity) =>
      update({ browserIdentity }),
    [update]
  );

  return {
    state,
    start,
    end,
    onConnected,
    onDisconnected,
    setBrowserIdentity,
  };
};
